import { useEffect, useState } from 'react';
import { Icon } from '../components/Icon';
import { api, ApiError } from '../lib/api';

interface SourceStatus { row_count: number; last_synced: string | null }
interface BackfillJob { job_type: string; total: number; complete: number; in_progress: number; failed: number }
interface RecentAction { created_at: string; action: string; target_type: string | null; target_id: string | null; user_email: string | null }
interface HealthResponse {
  db: { ok: boolean };
  geocoder: { loadedStates: string[]; polygonCount: number; ready: boolean };
  sources: {
    zoho_franchisees: SourceStatus;
    zoho_axiscare_sites: SourceStatus;
    zoho_territory_zips: SourceStatus;
    zoho_exception_ledger: SourceStatus;
    axiscare_visits: SourceStatus;
    axiscare_clients: SourceStatus;
  };
  backfill_jobs: BackfillJob[];
  recent_actions: RecentAction[];
}

export function SystemHealth() {
  const [data, setData] = useState<HealthResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [syncing, setSyncing] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<string | null>(null);

  const load = async () => {
    setError(null);
    try {
      setData(await api<HealthResponse>('/api/admin/system-health'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load health');
    }
  };

  useEffect(() => { load(); }, []);

  const runSync = async (target: 'franchisees' | 'spc') => {
    setSyncing(target); setSyncResult(null);
    try {
      if (target === 'spc') {
        const result = await api<{ envelopesScanned: number; upserted: number; skippedMissingAf: number; skippedNoScope: number }>(`/api/admin/sync/spc`, { method: 'POST' });
        setSyncResult(`Synced ${result.upserted} SPCs from Zoho Sign (${result.skippedMissingAf} skipped: missing AF#, ${result.skippedNoScope} skipped: no scope set).`);
      } else {
        const result = await api<{ fetched: number; upserted: number; withSite: number }>(`/api/admin/sync/${target}`, { method: 'POST' });
        setSyncResult(`Synced ${result.upserted} franchisees · ${result.withSite} unique AxisCare sites.`);
      }
      await load();
    } catch (err) {
      setSyncResult(`Sync failed: ${err instanceof Error ? err.message : 'unknown'}`);
    } finally { setSyncing(null); }
  };

  if (error) return (
    <div style={{ padding: 24 }}>
      <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>System health</h1>
      <div style={{ marginTop: 16, padding: 12, background: 'var(--danger-soft)', border: '1px solid color-mix(in oklch, var(--danger) 30%, var(--border))', borderRadius: 6, color: 'var(--danger)' }}>
        {error}
      </div>
    </div>
  );

  if (!data) return <div style={{ padding: 24 }} className="muted">Loading…</div>;

  const sources: { key: keyof HealthResponse['sources']; label: string; canSync?: 'franchisees' | 'spc' }[] = [
    { key: 'zoho_franchisees',     label: 'Zoho · Franchisees',         canSync: 'franchisees' },
    { key: 'zoho_axiscare_sites',  label: 'Zoho · AxisCare sites' },
    { key: 'zoho_territory_zips',  label: 'Zoho · Territory zip codes' },
    { key: 'zoho_exception_ledger', label: 'Zoho · Exception ledger (SPC + IR)', canSync: 'spc' },
    { key: 'axiscare_visits',      label: 'AxisCare · Visits' },
    { key: 'axiscare_clients',     label: 'AxisCare · Clients' },
  ];

  return (
    <div className="scroll-y" style={{ flex: 1, padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Admin</div>
          <h1 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 600 }}>System health</h1>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn" onClick={load}><Icon name="refresh" size={13} /> Refresh</button>
        </div>
      </div>

      {syncResult && (
        <div style={{ padding: 10, background: 'var(--accent-soft)', border: '1px solid var(--accent)', borderRadius: 6, fontSize: 13 }}>
          {syncResult}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        <div className="kpi">
          <div className="kpi-label">Database</div>
          <div className="kpi-value c-ok">{data.db.ok ? 'OK' : 'DOWN'}</div>
          <div className="kpi-sub">MariaDB · afi_encroachment schema</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Geocoder (ZCTA)</div>
          <div className={`kpi-value ${data.geocoder.ready ? 'c-ok' : 'c-warn'}`}>{data.geocoder.ready ? 'Ready' : 'Not ready'}</div>
          <div className="kpi-sub">{data.geocoder.polygonCount.toLocaleString()} polygons · {data.geocoder.loadedStates.join(', ') || 'no states loaded'}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Backfill jobs</div>
          <div className="kpi-value">{data.backfill_jobs.reduce((s, j) => s + j.total, 0)}</div>
          <div className="kpi-sub">
            {data.backfill_jobs.length === 0 ? 'none queued' : data.backfill_jobs.map(j => `${j.complete}/${j.total} ${j.job_type}`).join(' · ')}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="sec-head"><h3>Data sources</h3></div>
        <table className="tbl">
          <thead>
            <tr><th>Source</th><th style={{ textAlign: 'right' }}>Rows</th><th>Last sync</th><th>Action</th></tr>
          </thead>
          <tbody>
            {sources.map(({ key, label, canSync }) => {
              const s = data.sources[key];
              const synced = s.last_synced ? new Date(s.last_synced) : null;
              return (
                <tr key={key}>
                  <td>{label}</td>
                  <td className="num mono" style={{ textAlign: 'right' }}>{s.row_count.toLocaleString()}</td>
                  <td className="muted mono" style={{ fontSize: 12 }}>
                    {synced ? `${synced.toLocaleString()} (${ago(synced)})` : <em>never</em>}
                  </td>
                  <td>
                    {canSync && (
                      <button className="btn btn-sm" disabled={syncing === canSync} onClick={() => runSync(canSync)}>
                        {syncing === canSync ? 'Syncing…' : 'Sync now'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="card">
        <div className="sec-head"><h3>Recent admin actions</h3></div>
        <table className="tbl">
          <thead>
            <tr><th>When</th><th>Who</th><th>Action</th><th>Target</th></tr>
          </thead>
          <tbody>
            {data.recent_actions.length === 0 && (
              <tr><td colSpan={4} className="muted" style={{ textAlign: 'center', padding: 20 }}>No actions yet.</td></tr>
            )}
            {data.recent_actions.map((a, i) => (
              <tr key={i}>
                <td className="muted mono" style={{ fontSize: 12 }}>{new Date(a.created_at).toLocaleString()}</td>
                <td>{a.user_email ?? <em>system</em>}</td>
                <td className="mono" style={{ fontSize: 12.5 }}>{a.action}</td>
                <td className="muted mono" style={{ fontSize: 12 }}>{a.target_type ? `${a.target_type}#${a.target_id}` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ago(d: Date): string {
  const m = Math.floor((Date.now() - d.getTime()) / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
